import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Group, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBell, IconBellRinging } from '@tabler/icons-react';
import { activeChannels } from '@/lib/priceAlert';
import { usePriceAlerts } from '@/hooks/usePriceAlert';
import { useProfile } from '@/hooks/useProfile';
import type { PriceAlertChannel } from '@/types/priceAlert';
import BestPriceAlertModal from '@/components/app/sell/BestPriceAlertModal';

// Компактная точка входа в подписку «лучшая цена продажи» — заметная (тонированная, сверху списка
// «Продам»), но в одну строку: не оттесняет офферы вниз. Статус кодируем иконкой+текстом+бейджами,
// не одним цветом (§9). Ошибку чтения проглатываем — показываем выключенный вид, фича не блокирует.
export default function BestPriceAlertCard() {
  const { t } = useTranslation();
  const { data: subscriptions, isLoading } = usePriceAlerts();
  const { data: profile } = useProfile();
  const [opened, { open, close }] = useDisclosure(false);

  if (isLoading) {
    return <Skeleton h={62} radius="md" />;
  }

  const subs = subscriptions ?? [];
  const channels = activeChannels(subs);
  const active = channels.length > 0;

  const channelLabel = (channel: PriceAlertChannel): string =>
    channel === 'WHATSAPP' ? t('app.sell.priceAlert.whatsapp') : t('app.sell.priceAlert.email');

  return (
    <Card withBorder padding="sm" radius="md" bg={active ? 'brand.0' : 'lime.0'}>
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
          <ThemeIcon variant="light" color={active ? 'brand' : 'lime'} size={38} radius="md" style={{ flexShrink: 0 }}>
            {active ? <IconBellRinging size={22} /> : <IconBell size={22} />}
          </ThemeIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fw={600} size="sm">
              {active ? t('app.sell.priceAlert.cardTitleOn') : t('app.sell.priceAlert.cardTitleOff')}
            </Text>
            {active ? (
              <Group gap={6} wrap="wrap">
                {channels.map((channel) => (
                  <Badge key={channel} variant="light" color="brand" tt="none" fw={500} radius="sm">
                    {channelLabel(channel)}
                  </Badge>
                ))}
              </Group>
            ) : (
              // На узком экране пояснение прячем: баннер занимал полэкрана над списком предложений.
              <Text size="sm" c="gray.7" visibleFrom="sm">
                {t('app.sell.priceAlert.cardSubtitleOff')}
              </Text>
            )}
          </Stack>
        </Group>
        {/* Всегда вторичная: заливка lime — у главного действия страницы «Создать предложение», второй такой
            кнопки рядом быть не должно. */}
        <Button variant="default" size="sm" onClick={open} style={{ flexShrink: 0 }}>
          {active ? t('app.sell.priceAlert.edit') : t('app.sell.priceAlert.configure')}
        </Button>
      </Group>

      <BestPriceAlertModal opened={opened} onClose={close} subscriptions={subs} fallbackEmail={profile?.email ?? ''} />
    </Card>
  );
}
